import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Send, CheckCircle, Clock, Search, RefreshCw, FileText, XCircle,
  AlertTriangle, ChevronDown, ChevronUp, ArrowUpDown, Users, Receipt,
  CreditCard, RotateCcw, RotateCw, ChevronRight, Link2, Pencil, ShoppingCart,
  Columns3, Check, ChevronLeft
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import apiClient from '../api/client';

const ENTITY_TABS = [
  { key: 'invoices', label: 'Invoices', icon: FileText, singularPost: 'invoice' },
  { key: 'vendors', label: 'Vendors', icon: Users, singularPost: 'vendor' },
  { key: 'expenses', label: 'Expenses', icon: CreditCard, singularPost: 'expense' },
];

// ── Invoice column picker ──────────────────────────────────────────────────
const POSTING_COL_KEY = 'spocodoc_posting_invoice_columns';
const INVOICE_COLUMNS = [
  { key: 'invoiceNumber', label: 'Invoice #',      default: true,  locked: true },
  { key: 'vendor',        label: 'Vendor',          default: true  },
  { key: 'vendorStatus',  label: 'Vendor Status',   default: true  },
  { key: 'date',          label: 'Date',            default: true  },
  { key: 'amount',        label: 'Amount',          default: true  },
  { key: 'currency',      label: 'Currency',        default: false },
  { key: 'department',    label: 'Department',      default: false },
  { key: 'glAccount',     label: 'GL Account',      default: true  },
  { key: 'purchaseOrder', label: 'Purchase Order',  default: true  },
  { key: 'erpPayment',    label: 'ERP Payment',     default: false },
];
const INVOICE_COL_DEFAULTS = INVOICE_COLUMNS.filter(c => c.default).map(c => c.key);
function loadPostingColPrefs() {
  try {
    const saved = JSON.parse(localStorage.getItem(POSTING_COL_KEY) || 'null');
    if (!Array.isArray(saved)) return INVOICE_COL_DEFAULTS;
    const locked = INVOICE_COLUMNS.filter(c => c.locked).map(c => c.key);
    return [...new Set([...locked, ...saved.filter(k => INVOICE_COLUMNS.some(c => c.key === k))])];
  } catch { return INVOICE_COL_DEFAULTS; }
}

// ── Vendor column picker ─────────────────────────────────────────────────────
const VENDOR_COL_KEY = 'spocodoc_posting_vendor_columns';
const VENDOR_COLUMNS = [
  { key: 'vendorName',   label: 'Vendor Name',   default: true,  locked: true },
  { key: 'email',        label: 'Email',         default: true  },
  { key: 'phone',        label: 'Phone',         default: false },
  { key: 'taxId',        label: 'Tax ID',        default: true  },
  { key: 'paymentTerms', label: 'Payment Terms', default: false },
  { key: 'invoiceCount', label: 'Invoices',      default: true  },
  { key: 'postedAt',     label: 'Posted Date',   default: true  },
  { key: 'postedBy',     label: 'Posted By',     default: false },
  { key: 'externalId',   label: 'External ID',   default: true  },
  { key: 'createdAt',    label: 'Created',       default: true  },
];
const VENDOR_COL_DEFAULTS = VENDOR_COLUMNS.filter(c => c.default).map(c => c.key);

// ── Expense column picker ────────────────────────────────────────────────────
const EXPENSE_COL_KEY = 'spocodoc_posting_expense_columns';
const EXPENSE_COLUMNS = [
  { key: 'expenseNumber', label: 'Expense #',   default: true,  locked: true },
  { key: 'employee',      label: 'Employee',    default: true  },
  { key: 'date',          label: 'Date',        default: true  },
  { key: 'amount',        label: 'Amount',      default: true  },
  { key: 'currency',      label: 'Currency',    default: false },
  { key: 'purpose',       label: 'Purpose',     default: true  },
  { key: 'category',      label: 'Category',    default: true  },
  { key: 'department',    label: 'Department',  default: true  },
  { key: 'postedAt',      label: 'Posted Date', default: true  },
  { key: 'postedBy',      label: 'Posted By',   default: false },
  { key: 'externalId',    label: 'External ID', default: true  },
];
const EXPENSE_COL_DEFAULTS = EXPENSE_COLUMNS.filter(c => c.default).map(c => c.key);

function loadColPrefs(storageKey, colDefs) {
  try {
    const saved = JSON.parse(localStorage.getItem(storageKey) || 'null');
    if (!Array.isArray(saved)) return colDefs.filter(c => c.default).map(c => c.key);
    const locked = colDefs.filter(c => c.locked).map(c => c.key);
    return [...new Set([...locked, ...saved.filter(k => colDefs.some(c => c.key === k))])];
  } catch { return colDefs.filter(c => c.default).map(c => c.key); }
}

export default function InvoicePostingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Top-level entity tab: invoices | vendors | expenses
  const [entityType, setEntityType] = useState('invoices');
  // Sub-tab: ready | posted
  const [subTab, setSubTab] = useState('ready');

  // Data
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({});
  const [activeIntegration, setActiveIntegration] = useState(null);
  const [vendorStatusMap, setVendorStatusMap] = useState({});

  // UI state
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [repostingIds, setRepostingIds] = useState(new Set());
  const [revertingIds, setRevertingIds] = useState(new Set());
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [autoPostVendor, setAutoPostVendor] = useState(true);

  // Purchase Orders from BC
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [poLoading, setPoLoading] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 25;

  // Sort
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('desc');

  // Column picker — per entity type
  const COL_CONFIG = {
    invoices: { key: POSTING_COL_KEY, cols: INVOICE_COLUMNS, defaults: INVOICE_COL_DEFAULTS },
    vendors:  { key: VENDOR_COL_KEY,  cols: VENDOR_COLUMNS,  defaults: VENDOR_COL_DEFAULTS  },
    expenses: { key: EXPENSE_COL_KEY, cols: EXPENSE_COLUMNS, defaults: EXPENSE_COL_DEFAULTS },
  };

  const [colVisibility, setColVisibility] = useState(() => ({
    invoices: loadColPrefs(POSTING_COL_KEY, INVOICE_COLUMNS),
    vendors:  loadColPrefs(VENDOR_COL_KEY,  VENDOR_COLUMNS),
    expenses: loadColPrefs(EXPENSE_COL_KEY, EXPENSE_COLUMNS),
  }));
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const columnPickerRef = useRef(null);

  const visibleColumns = colVisibility[entityType] || [];
  const colIsVisible = (key) => visibleColumns.includes(key);

  const toggleColumn = (key) => {
    const cfg = COL_CONFIG[entityType];
    if (!cfg) return;
    if (cfg.cols.find(c => c.key === key)?.locked) return;
    setColVisibility(prev => {
      const current = prev[entityType] || [];
      const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
      localStorage.setItem(cfg.key, JSON.stringify(next));
      return { ...prev, [entityType]: next };
    });
  };

  const resetColumns = () => {
    const cfg = COL_CONFIG[entityType];
    if (!cfg) return;
    setColVisibility(prev => ({ ...prev, [entityType]: cfg.defaults }));
    localStorage.setItem(cfg.key, JSON.stringify(cfg.defaults));
  };

  // Close column picker on outside click
  useEffect(() => {
    function handler(e) {
      if (columnPickerRef.current && !columnPickerRef.current.contains(e.target)) setShowColumnPicker(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Fetchers ──────────────────────────────────────────────────────────

  const fetchItems = useCallback(async () => {
    try {
      const endpoint = subTab === 'ready' ? 'ready' : 'posted';
      const params = new URLSearchParams({ entityType, page, pageSize, search });
      if (sortField) {
        params.append('sortField', sortField);
        params.append('sortDir', sortDir);
      }
      const res = await apiClient.get(`/invoice-posting/${endpoint}?${params}`);
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      if (res.data.activeIntegration !== undefined) {
        setActiveIntegration(res.data.activeIntegration);
      }
    } catch (err) {
      console.error(`Failed to load ${entityType}:`, err);
      setItems([]);
      setTotal(0);
    }
  }, [entityType, subTab, page, search, sortField, sortDir]);

  const fetchStats = useCallback(async () => {
    try {
      const res = await apiClient.get('/invoice-posting/stats');
      setStats(res.data || {});
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }, []);

  const fetchVendorStatus = useCallback(async () => {
    try {
      const res = await apiClient.get('/invoice-posting/vendor-status');
      const map = {};
      (res.data.vendors || []).forEach(v => {
        map[v.VendorID] = v;
      });
      setVendorStatusMap(map);
    } catch {
      // non-critical
    }
  }, []);

  const fetchPurchaseOrders = useCallback(async () => {
    try {
      setPoLoading(true);
      const res = await apiClient.get('/invoice-posting/purchase-orders');
      setPurchaseOrders(res.data.purchaseOrders || []);
    } catch (err) {
      console.error('Failed to load purchase orders:', err);
    } finally {
      setPoLoading(false);
    }
  }, []);

  // ── Effects ────────────────────────────────────────────────────────────

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchItems(), fetchStats(), fetchVendorStatus(), fetchPurchaseOrders()]);
      setLoading(false);
    };
    init();
  }, []);

  useEffect(() => {
    setSelectedIds(new Set());
    setPage(1);
    setSearch('');
    setSortField('');
  }, [entityType, subTab]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // ── Handlers ───────────────────────────────────────────────────────────

  const refreshAll = async () => {
    await Promise.all([fetchItems(), fetchStats(), fetchVendorStatus(), fetchPurchaseOrders()]);
  };

  const getEntityIdField = () => {
    if (entityType === 'invoices') return 'InvoiceID';
    if (entityType === 'vendors') return 'VendorID';
    return 'ExpenseID';
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const idField = getEntityIdField();
    if (selectedIds.size === items.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(i => i[idField])));
    }
  };

  const singularType = ENTITY_TABS.find(t => t.key === entityType)?.singularPost || 'invoice';

  // ── POST ───────────────────────────────────────────────────────────────

  const handlePost = async () => {
    if (selectedIds.size === 0) return alert('Please select at least one item to post.');
    if (!activeIntegration) return alert('No active finance integration. Configure one in Finance Integrations.');

    const label = `${selectedIds.size} ${singularType}(s)`;
    if (!confirm(`Post ${label} to ${activeIntegration.DisplayName}?`)) return;

    setPosting(true);
    try {
      const body = {
        entityType: singularType,
        entityIds: Array.from(selectedIds),
      };
      if (entityType === 'invoices') {
        body.options = { autoPostVendor };
      }
      const res = await apiClient.post('/invoice-posting/post', body);
      const { posted, failed, errors, vendorsAutoPosted, vendorAutoPostFailed } = res.data;

      let msg = `${posted} ${singularType}(s) posted successfully.`;
      if (vendorsAutoPosted) msg += ` ${vendorsAutoPosted} vendor(s) auto-posted.`;
      if (vendorAutoPostFailed) msg += `\n\n⚠️ ${vendorAutoPostFailed} vendor(s) could not be auto-posted. See errors below — post them manually from the Vendors tab.`;
      if (errors?.length) {
        msg += '\n\nDetails:';
        errors.forEach(e => { msg += `\n• ${e.name || e.entityId}: ${e.error}`; });
      }
      alert(msg);
      setSelectedIds(new Set());
      await refreshAll();
    } catch (err) {
      alert(err.response?.data?.message || `Failed to post ${singularType}(s).`);
    } finally {
      setPosting(false);
    }
  };

  // ── REPOST ─────────────────────────────────────────────────────────────

  const handleRepost = async (ids) => {
    const arr = Array.isArray(ids) ? ids : [ids];
    if (!confirm(`Update ${arr.length} ${singularType}(s) in ${activeIntegration?.DisplayName || 'finance tool'}?`)) return;

    setRepostingIds(prev => new Set([...prev, ...arr]));
    try {
      const res = await apiClient.post('/invoice-posting/repost', {
        entityType: singularType,
        entityIds: arr,
      });
      const { posted, failed, errors } = res.data;
      let msg = `${posted} ${singularType}(s) updated successfully.`;
      if (failed > 0 && errors?.length) {
        msg += '\n\nFailed:';
        errors.forEach(e => { msg += `\n• ${e.name || e.entityId}: ${e.error}`; });
      }
      alert(msg);
      await refreshAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Update failed.');
    } finally {
      setRepostingIds(prev => {
        const next = new Set(prev);
        arr.forEach(id => next.delete(id));
        return next;
      });
    }
  };

  // ── REVERT ─────────────────────────────────────────────────────────────

  const handleRevert = async (ids) => {
    const arr = Array.isArray(ids) ? ids : [ids];
    if (!confirm(`Revert ${arr.length} ${singularType}(s)? This will remove the posted status and the external reference.`)) return;

    setRevertingIds(prev => new Set([...prev, ...arr]));
    try {
      const res = await apiClient.post('/invoice-posting/revert', {
        entityType: singularType,
        entityIds: arr,
      });
      const { reverted, failed, errors } = res.data;
      let msg = `${reverted} ${singularType}(s) reverted successfully.`;
      if (failed > 0 && errors?.length) {
        msg += '\n\nFailed:';
        errors.forEach(e => { msg += `\n• ${e.name || e.entityId}: ${e.error}`; });
      }
      alert(msg);
      await refreshAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Revert failed.');
    } finally {
      setRevertingIds(prev => {
        const next = new Set(prev);
        arr.forEach(id => next.delete(id));
        return next;
      });
    }
  };

  // ── BULK ACTIONS for Posted ────────────────────────────────────────────

  const handleBulkRepost = () => {
    if (selectedIds.size === 0) return alert('Select items first.');
    handleRepost(Array.from(selectedIds));
  };

  const handleBulkRevert = () => {
    if (selectedIds.size === 0) return alert('Select items first.');
    handleRevert(Array.from(selectedIds));
  };

  // ── FETCH PAYMENT STATUS (invoices / posted tab only) ──────────────────

  const [fetchingPayment, setFetchingPayment] = useState(false);

  const handleFetchPaymentStatus = async () => {
    if (selectedIds.size === 0) return alert('Select invoices first.');
    if (!activeIntegration) return alert('No active ERP integration configured.');
    setFetchingPayment(true);
    try {
      const res = await apiClient.post('/invoice-posting/sync-payment-status', {
        invoiceIds: Array.from(selectedIds),
      });
      const { synced, failed, skipped, results } = res.data;
      let msg = `Payment status fetched:\n• ${synced} updated`;
      if (skipped > 0) msg += `\n• ${skipped} skipped (no ERP link)`;
      if (failed > 0) {
        msg += `\n• ${failed} failed:`;
        results.filter(r => r.error).forEach(r => {
          msg += `\n  – ${r.invoiceNumber || r.invoiceId}: ${r.error}`;
        });
      }
      alert(msg);
      await refreshAll();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to fetch payment status.');
    } finally {
      setFetchingPayment(false);
    }
  };

  // ── Sort ────────────────────────────────────────────────────────────────

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────

  const SortHeader = ({ field, children, className = '' }) => (
    <th
      className={`px-4 py-3 font-medium text-gray-600 cursor-pointer select-none ${className}`}
      onClick={() => handleSort(field)}
    >
      <span className="flex items-center">
        {children}
        {sortField === field ? (
          sortDir === 'asc' ? <ChevronUp className="w-3.5 h-3.5 ml-1" /> : <ChevronDown className="w-3.5 h-3.5 ml-1" />
        ) : (
          <ArrowUpDown className="w-3.5 h-3.5 ml-1 text-gray-300" />
        )}
      </span>
    </th>
  );

  const formatCurrency = (amount, currency = 'GBP') => {
    if (amount == null) return '—';
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency || 'GBP' }).format(amount);
  };

  const formatDate = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatDateTime = (d) => {
    if (!d) return '—';
    return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const totalPages = Math.ceil(total / pageSize) || 1;

  // ── Stat helpers ────────────────────────────────────────────────────────

  const statCards = [
    { key: 'Invoices', icon: FileText, color: 'blue' },
    { key: 'Vendors', icon: Users, color: 'purple' },
    { key: 'Expenses', icon: CreditCard, color: 'teal' },
  ];

  const getStatReady = (key) => stats[`${key}Ready`] || 0;
  const getStatPosted = (key) => stats[`${key}Posted`] || 0;
  const getStatFailed = (key) => stats[`${key}Failed`] || 0;

  // ── Vendor posted badge for invoices ───────────────────────────────────

  const VendorBadge = ({ vendorId, vendorPostedStatus }) => {
    if (!vendorId) return <span className="text-xs text-gray-400">No vendor</span>;
    const isPosted = vendorPostedStatus === 'Posted';
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-xs rounded-full ${
        isPosted ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
      }`}>
        {isPosted ? <CheckCircle className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
        {isPosted ? 'Posted' : 'Not Posted'}
      </span>
    );
  };

  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-600 mr-2" />
        <span className="text-gray-600">Loading finance posting...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back navigation */}
      {location.state?.from && (
        <button
          onClick={() => navigate(location.state.from)}
          className="flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 font-medium group"
        >
          <ChevronLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          Back to {location.state.fromLabel || 'Previous Page'}
        </button>
      )}
      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Send className="w-7 h-7 text-blue-600" />
            Post to Finance
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Post invoices, vendors, and expenses to your finance system
          </p>
        </div>
        <button
          onClick={refreshAll}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* ── Integration Banner ──────────────────────────────────────────── */}
      {activeIntegration ? (
        <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
          <div className="text-sm">
            <span className="text-green-800 font-medium">Connected to: </span>
            <span className="text-green-700 font-semibold">{activeIntegration.DisplayName}</span>
            {activeIntegration.Provider && (
              <span className="text-green-600 ml-2">({activeIntegration.Provider.replace('_', ' ')})</span>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
          <div className="text-sm">
            <span className="text-amber-800 font-medium">No finance integration active. </span>
            <a href="/finance-integrations" className="text-blue-600 underline hover:text-blue-700">
              Configure one in Finance Integrations
            </a>
          </div>
        </div>
      )}

      {/* ── Stats Dashboard ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {statCards.map(({ key, icon: Icon, color }) => (
          <div key={key} className="bg-white rounded-lg border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon className={`w-5 h-5 text-${color}-600`} />
              <span className="font-semibold text-gray-800">{key}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div>
                <div className="text-xs text-gray-500 mb-0.5">Ready</div>
                <div className="text-lg font-bold text-gray-900">{getStatReady(key)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-0.5">Posted</div>
                <div className="text-lg font-bold text-green-700">{getStatPosted(key)}</div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-0.5">Failed</div>
                <div className="text-lg font-bold text-red-600">{getStatFailed(key)}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ── Entity Type Tabs ────────────────────────────────────────────── */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {ENTITY_TABS.map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setEntityType(key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-md transition-all ${
              entityType === key
                ? 'bg-white shadow text-blue-700'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>

      {/* ── Sub-Tabs: Ready / Posted ────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex gap-1 bg-gray-50 p-0.5 rounded-lg border border-gray-200">
          <button
            onClick={() => setSubTab('ready')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
              subTab === 'ready'
                ? 'bg-white shadow-sm text-blue-700 border border-gray-200'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <Clock className="w-3.5 h-3.5" />
            Ready to Post
            <span className="ml-1 px-1.5 py-0 text-xs rounded-full bg-blue-100 text-blue-700">
              {getStatReady(ENTITY_TABS.find(t => t.key === entityType)?.label || 'Invoices')}
            </span>
          </button>
          <button
            onClick={() => setSubTab('posted')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
              subTab === 'posted'
                ? 'bg-white shadow-sm text-green-700 border border-gray-200'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            <CheckCircle className="w-3.5 h-3.5" />
            Posted
            <span className="ml-1 px-1.5 py-0 text-xs rounded-full bg-green-100 text-green-700">
              {getStatPosted(ENTITY_TABS.find(t => t.key === entityType)?.label || 'Invoices')}
            </span>
          </button>
        </div>
      </div>

      {/* ── Search + Actions Bar ────────────────────────────────────────── */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={
              entityType === 'invoices' ? 'Search invoice number, vendor, reference...' :
              entityType === 'vendors' ? 'Search vendor name, email...' :
              'Search expense number, purpose...'
            }
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
          />
        </div>

        {/* Column picker — all entity types */}
        <div className="relative" ref={columnPickerRef}>
          <button
            onClick={() => setShowColumnPicker(p => !p)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm border rounded-lg transition-colors ${showColumnPicker ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'}`}
            title="Show/hide columns"
          >
            <Columns3 className="w-4 h-4" />
            Columns
          </button>
          {showColumnPicker && (
            <div className="absolute left-0 top-10 z-30 w-52 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Columns</span>
                <button onClick={resetColumns} className="text-xs text-blue-600 hover:underline">Reset</button>
              </div>
              <div className="py-1">
                {(COL_CONFIG[entityType]?.cols || INVOICE_COLUMNS).map(col => (
                  <button
                    key={col.key}
                    onClick={() => toggleColumn(col.key)}
                    disabled={col.locked}
                    className="w-full flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-default transition-colors"
                  >
                    <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${colIsVisible(col.key) ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                      {colIsVisible(col.key) && <Check className="w-3 h-3 text-white" />}
                    </span>
                    {col.label}
                    {col.locked && <span className="ml-auto text-[10px] text-gray-400">locked</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Auto-post vendor toggle (only for invoices ready tab) */}
        {entityType === 'invoices' && subTab === 'ready' && (
          <label className="flex items-center gap-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2 cursor-pointer">
            <input
              type="checkbox"
              checked={autoPostVendor}
              onChange={(e) => setAutoPostVendor(e.target.checked)}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            Auto-post vendor
          </label>
        )}

        {/* Action buttons */}
        {subTab === 'ready' && selectedIds.size > 0 && (
          <button
            onClick={handlePost}
            disabled={posting || !activeIntegration}
            className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {posting ? (
              <><RefreshCw className="w-4 h-4 animate-spin" /> Posting {selectedIds.size}...</>
            ) : (
              <><Send className="w-4 h-4" /> Post {selectedIds.size} Selected</>
            )}
          </button>
        )}

        {subTab === 'posted' && selectedIds.size > 0 && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkRepost}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Pencil className="w-4 h-4" /> Update {selectedIds.size}
            </button>
            {entityType === 'invoices' && (
              <button
                onClick={handleFetchPaymentStatus}
                disabled={fetchingPayment || !activeIntegration}
                title={!activeIntegration ? 'No active ERP integration' : 'Fetch payment status from ERP for selected invoices'}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {fetchingPayment
                  ? <><RefreshCw className="w-4 h-4 animate-spin" /> Fetching...</>
                  : <><RefreshCw className="w-4 h-4" /> Fetch Payment Status</>}
              </button>
            )}
            <button
              onClick={handleBulkRevert}
              className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
            >
              <RotateCcw className="w-4 h-4" /> Revert {selectedIds.size}
            </button>
          </div>
        )}
      </div>

      {/* ══════════════════════════════════════════════════════════════════ */}
      {/* TABLES                                                           */}
      {/* ══════════════════════════════════════════════════════════════════ */}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          {/* ── INVOICES ─────────────────────────────────────────────────── */}
          {entityType === 'invoices' && subTab === 'ready' && (
            <InvoiceReadyTable
              items={items} selectedIds={selectedIds}
              toggleSelect={toggleSelect} toggleSelectAll={toggleSelectAll}
              SortHeader={SortHeader} formatCurrency={formatCurrency} formatDate={formatDate}
              VendorBadge={VendorBadge}
              purchaseOrders={purchaseOrders} poLoading={poLoading}
              visibleColumns={visibleColumns}
            />
          )}
          {entityType === 'invoices' && subTab === 'posted' && (
            <InvoicePostedTable
              items={items} selectedIds={selectedIds}
              toggleSelect={toggleSelect} toggleSelectAll={toggleSelectAll}
              formatCurrency={formatCurrency} formatDate={formatDate} formatDateTime={formatDateTime}
              handleRepost={handleRepost} handleRevert={handleRevert}
              repostingIds={repostingIds} revertingIds={revertingIds}
              visibleColumns={visibleColumns}
              provider={activeIntegration?.Provider}
            />
          )}

          {/* ── VENDORS ──────────────────────────────────────────────────── */}
          {entityType === 'vendors' && subTab === 'ready' && (
            <VendorReadyTable
              items={items} selectedIds={selectedIds}
              toggleSelect={toggleSelect} toggleSelectAll={toggleSelectAll}
              SortHeader={SortHeader} formatDate={formatDate}
              visibleColumns={visibleColumns}
            />
          )}
          {entityType === 'vendors' && subTab === 'posted' && (
            <VendorPostedTable
              items={items} selectedIds={selectedIds}
              toggleSelect={toggleSelect} toggleSelectAll={toggleSelectAll}
              formatDate={formatDate} formatDateTime={formatDateTime}
              handleRepost={handleRepost} handleRevert={handleRevert}
              repostingIds={repostingIds} revertingIds={revertingIds}
              provider={activeIntegration?.Provider}
              visibleColumns={visibleColumns}
            />
          )}

          {/* ── EXPENSES ─────────────────────────────────────────────────── */}
          {entityType === 'expenses' && subTab === 'ready' && (
            <ExpenseReadyTable
              items={items} selectedIds={selectedIds}
              toggleSelect={toggleSelect} toggleSelectAll={toggleSelectAll}
              SortHeader={SortHeader} formatCurrency={formatCurrency} formatDate={formatDate}
              visibleColumns={visibleColumns}
            />
          )}
          {entityType === 'expenses' && subTab === 'posted' && (
            <ExpensePostedTable
              items={items} selectedIds={selectedIds}
              toggleSelect={toggleSelect} toggleSelectAll={toggleSelectAll}
              formatCurrency={formatCurrency} formatDate={formatDate} formatDateTime={formatDateTime}
              handleRepost={handleRepost} handleRevert={handleRevert}
              repostingIds={repostingIds} revertingIds={revertingIds}
              visibleColumns={visibleColumns}
            />
          )}
        </div>

        {/* ── Pagination ─────────────────────────────────────────────────── */}
        {total > pageSize && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <span className="text-sm text-gray-500">
              Showing {((page - 1) * pageSize) + 1}–{Math.min(page * pageSize, total)} of {total}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-40"
              >Previous</button>
              <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-40"
              >Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS: Invoice Tables
// ═══════════════════════════════════════════════════════════════════════════════

function InvoiceReadyTable({ items, selectedIds, toggleSelect, toggleSelectAll, SortHeader, formatCurrency, formatDate, VendorBadge, purchaseOrders, poLoading, visibleColumns }) {
  const col = (key) => !visibleColumns || visibleColumns.includes(key);
  const [linkingPO, setLinkingPO] = useState({});
  const [autoMatched, setAutoMatched] = useState({}); // Track which invoices were auto-matched
  const autoMatchedRef = useRef(new Set()); // Prevent duplicate auto-match calls

  const handlePOChange = async (invoiceId, poNumber) => {
    setLinkingPO(prev => ({ ...prev, [invoiceId]: true }));
    try {
      await apiClient.patch('/invoice-posting/link-po', {
        invoiceId,
        purchaseOrder: poNumber || null
      });
      // Update the item in-place
      const item = items.find(i => i.InvoiceID === invoiceId);
      if (item) item.PurchaseOrder = poNumber || null;
      // Clear auto-matched flag if user manually changes
      setAutoMatched(prev => ({ ...prev, [invoiceId]: false }));
    } catch (err) {
      console.error('Failed to link PO:', err);
    } finally {
      setLinkingPO(prev => ({ ...prev, [invoiceId]: false }));
    }
  };

  // Auto-match: find PO with same vendor + same amount for invoices without a PO
  useEffect(() => {
    if (!purchaseOrders.length || !items.length || poLoading) return;

    const toMatch = items.filter(inv => !inv.PurchaseOrder && !autoMatchedRef.current.has(inv.InvoiceID));
    if (!toMatch.length) return;

    toMatch.forEach(inv => {
      const vendorName = (inv.VendorName || '').toLowerCase().trim();
      const invoiceTotal = parseFloat(inv.TotalAmount) || 0;
      if (!vendorName || !invoiceTotal) return;

      // Find PO with matching vendor AND matching total (within 0.01 tolerance for rounding)
      const match = purchaseOrders.find(po => {
        const poVendor = (po.vendorName || '').toLowerCase().trim();
        const poTotal = parseFloat(po.totalAmountIncludingTax) || 0;
        return poVendor === vendorName && Math.abs(poTotal - invoiceTotal) < 0.02;
      });

      if (match) {
        autoMatchedRef.current.add(inv.InvoiceID);
        console.log(`[PO Auto-Match] Invoice ${inv.InvoiceNumber} → PO ${match.number} (vendor: ${inv.VendorName}, amount: ${invoiceTotal})`);
        // Auto-select and save
        inv.PurchaseOrder = match.number;
        setAutoMatched(prev => ({ ...prev, [inv.InvoiceID]: true }));
        // Save to backend (fire and forget)
        apiClient.patch('/invoice-posting/link-po', {
          invoiceId: inv.InvoiceID,
          purchaseOrder: match.number
        }).catch(err => console.error('Auto-match save failed:', err));
      }
    });
  }, [purchaseOrders, items, poLoading]);

  // Build PO options filtered by vendor name for each invoice
  const getPOsForInvoice = (inv) => {
    if (!purchaseOrders.length) return purchaseOrders;
    // Show all POs but prioritize those matching the vendor
    const vendorName = (inv.VendorName || '').toLowerCase();
    return purchaseOrders.sort((a, b) => {
      const aMatch = (a.vendorName || '').toLowerCase() === vendorName ? 0 : 1;
      const bMatch = (b.vendorName || '').toLowerCase() === vendorName ? 0 : 1;
      return aMatch - bMatch || a.number.localeCompare(b.number);
    });
  };

  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="px-4 py-3 text-left w-10">
            <input
              type="checkbox"
              checked={items.length > 0 && selectedIds.size === items.length}
              onChange={toggleSelectAll}
              className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
          </th>
          <SortHeader field="InvoiceNumber">Invoice #</SortHeader>
          {col('vendor') && <SortHeader field="VendorName">Vendor</SortHeader>}
          {col('vendorStatus') && <th className="px-4 py-3 text-left font-medium text-gray-600">Vendor Status</th>}
          {col('date') && <SortHeader field="InvoiceDate">Date</SortHeader>}
          {col('amount') && <SortHeader field="TotalAmount" className="text-right">Amount</SortHeader>}
          {col('currency') && <th className="px-4 py-3 text-left font-medium text-gray-600">Currency</th>}
          {col('department') && <th className="px-4 py-3 text-left font-medium text-gray-600">Department</th>}
          {col('glAccount') && <th className="px-4 py-3 text-left font-medium text-gray-600">GL Account</th>}
          {col('purchaseOrder') && <th className="px-4 py-3 text-left font-medium text-gray-600">
            <div className="flex items-center gap-1">
              <ShoppingCart className="w-3.5 h-3.5" />Purchase Order
            </div>
          </th>}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {items.length === 0 ? (
          <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-400">
            <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />No approved invoices ready to post
          </td></tr>
        ) : items.map((inv, idx) => (
          <tr key={inv.InvoiceID || idx} className={`hover:bg-gray-50 transition-colors ${selectedIds.has(inv.InvoiceID) ? 'bg-blue-50/50' : ''}`}>
            <td className="px-4 py-3">
              <input type="checkbox" checked={selectedIds.has(inv.InvoiceID)} onChange={() => toggleSelect(inv.InvoiceID)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            </td>
            <td className="px-4 py-3 font-medium text-gray-900">{inv.InvoiceNumber || '—'}</td>
            {col('vendor') && <td className="px-4 py-3 text-gray-700">{inv.VendorName || '—'}</td>}
            {col('vendorStatus') && <td className="px-4 py-3"><VendorBadge vendorId={inv.VendorID} vendorPostedStatus={inv.VendorPostedStatus} /></td>}
            {col('date') && <td className="px-4 py-3 text-gray-600">{formatDate(inv.InvoiceDate)}</td>}
            {col('amount') && <td className="px-4 py-3 text-right font-mono text-gray-900">{formatCurrency(inv.TotalAmount, inv.Currency)}</td>}
            {col('currency') && <td className="px-4 py-3 text-gray-600">{inv.Currency || 'GBP'}</td>}
            {col('department') && <td className="px-4 py-3 text-gray-600">{inv.DepartmentName || '—'}</td>}
            {col('glAccount') && (
              <td className="px-4 py-3 text-gray-600 text-xs max-w-[180px] truncate" title={inv.GLAccounts || ''}>
                {inv.GLAccounts || '—'}
              </td>
            )}
            {col('purchaseOrder') && (
              <td className="px-4 py-3">
                {poLoading ? (
                  <span className="text-xs text-gray-400">Loading...</span>
                ) : purchaseOrders.length > 0 ? (
                  <div className="flex items-center gap-1">
                    <select
                      value={inv.PurchaseOrder || ''}
                      onChange={(e) => handlePOChange(inv.InvoiceID, e.target.value)}
                      disabled={linkingPO[inv.InvoiceID]}
                      className={`w-full min-w-[140px] px-2 py-1 text-xs border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white disabled:opacity-50 ${
                        autoMatched[inv.InvoiceID] ? 'border-green-400 bg-green-50' : 'border-gray-300'
                      }`}
                    >
                      <option value="">— None —</option>
                      {getPOsForInvoice(inv).map(po => (
                        <option key={po.id} value={po.number}>
                          {po.number} — {po.vendorName} ({formatCurrency(po.totalAmountIncludingTax, po.currencyCode || 'GBP')})
                        </option>
                      ))}
                    </select>
                    {autoMatched[inv.InvoiceID] && (
                      <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium text-green-700 bg-green-100 rounded-full" title="Auto-matched by vendor & amount">
                        <CheckCircle className="w-3 h-3" />Match
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">{inv.PurchaseOrder || '—'}</span>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function InvoicePostedTable({ items, selectedIds, toggleSelect, toggleSelectAll, formatCurrency, formatDate, formatDateTime, handleRepost, handleRevert, repostingIds, revertingIds, visibleColumns, provider }) {
  const col = (key) => !visibleColumns || visibleColumns.includes(key);
  const [editingPaymentId, setEditingPaymentId] = useState(null);
  const [savingPaymentId, setSavingPaymentId] = useState(null);
  const erpSaveRef = useRef(false);

  const handleInlinePaymentSave = async (invoiceId, newStatus) => {
    if (!newStatus) { setEditingPaymentId(null); return; }
    erpSaveRef.current = true;
    setSavingPaymentId(invoiceId);
    try {
      await apiClient.patch(`/invoices/${invoiceId}/payment-status`, { ERPPaymentStatus: newStatus });
      const item = items.find(i => i.InvoiceID === invoiceId);
      if (item) item.ERPPaymentStatus = newStatus;
    } catch (err) {
      console.error('Failed to update payment status', err);
    } finally {
      erpSaveRef.current = false;
      setSavingPaymentId(null);
      setEditingPaymentId(null);
    }
  };
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="px-4 py-3 text-left w-10">
            <input type="checkbox" checked={items.length > 0 && selectedIds.size === items.length}
              onChange={toggleSelectAll} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          </th>
          <th className="px-4 py-3 text-left font-medium text-gray-600">Invoice #</th>
          {col('vendor') && <th className="px-4 py-3 text-left font-medium text-gray-600">Vendor</th>}
          {col('date') && <th className="px-4 py-3 text-left font-medium text-gray-600">Date</th>}
          {col('amount') && <th className="px-4 py-3 text-right font-medium text-gray-600">Amount</th>}
          {col('glAccount') && <th className="px-4 py-3 text-left font-medium text-gray-600">GL Account</th>}
          {col('erpPayment') && <th className="px-4 py-3 text-left font-medium text-gray-600">ERP Payment</th>}
          <th className="px-4 py-3 text-left font-medium text-gray-600">Posted</th>
          <th className="px-4 py-3 text-left font-medium text-gray-600">Posted By</th>
          <th className="px-4 py-3 text-left font-medium text-gray-600">External Ref</th>
          <th className="px-4 py-3 text-right font-medium text-gray-600">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {items.length === 0 ? (
          <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-400">
            <Send className="w-10 h-10 mx-auto mb-2 text-gray-300" />No posted invoices
          </td></tr>
        ) : items.map((inv, idx) => (
          <tr key={inv.InvoiceID || idx} className={`hover:bg-gray-50 transition-colors ${selectedIds.has(inv.InvoiceID) ? 'bg-blue-50/50' : ''}`}>
            <td className="px-4 py-3">
              <input type="checkbox" checked={selectedIds.has(inv.InvoiceID)} onChange={() => toggleSelect(inv.InvoiceID)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            </td>
            <td className="px-4 py-3 font-medium text-gray-900">{inv.InvoiceNumber || '—'}</td>
            {col('vendor') && <td className="px-4 py-3 text-gray-700">{inv.VendorName || '—'}</td>}
            {col('date') && <td className="px-4 py-3 text-gray-600">{formatDate(inv.InvoiceDate)}</td>}
            {col('amount') && <td className="px-4 py-3 text-right font-mono text-gray-900">{formatCurrency(inv.TotalAmount, inv.Currency)}</td>}
            {col('glAccount') && (
              <td className="px-4 py-3 text-gray-600 text-xs max-w-[180px] truncate" title={inv.GLAccounts || ''}>
                {inv.GLAccounts || '—'}
              </td>
            )}
            {col('erpPayment') && (
              <td className="px-4 py-3">
                {editingPaymentId === inv.InvoiceID ? (
                  <select
                    autoFocus
                    disabled={savingPaymentId === inv.InvoiceID}
                    defaultValue={inv.ERPPaymentStatus || ''}
                    onBlur={() => { if (!erpSaveRef.current) setEditingPaymentId(null); }}
                    onChange={(e) => handleInlinePaymentSave(inv.InvoiceID, e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setEditingPaymentId(null); }}
                    className="text-xs border border-blue-400 rounded px-1 py-0.5 focus:outline-none"
                  >
                    <option value="">— Clear —</option>
                    <option value="Open">Open</option>
                    <option value="Paid">Paid</option>
                    <option value="PartiallyPaid">PartiallyPaid</option>
                    <option value="Voided">Voided</option>
                  </select>
                ) : (() => {
                  const s = inv.ERPPaymentStatus;
                  const cls =
                    s === 'Paid'          ? 'bg-green-100 text-green-800' :
                    s === 'PartiallyPaid' ? 'bg-yellow-100 text-yellow-800' :
                    s === 'Voided'        ? 'bg-red-100 text-red-700' :
                    s === 'Open'          ? 'bg-gray-100 text-gray-600' : '';
                  return (
                    <button
                      title="Click to edit"
                      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); setEditingPaymentId(inv.InvoiceID); }}
                      className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${cls || 'text-gray-400'} hover:ring-1 hover:ring-blue-400 transition-all`}
                    >
                      {s ? (s === 'PartiallyPaid' ? 'Part. Paid' : s) : '—'}
                    </button>
                  );
                })()}
              </td>
            )}
            <td className="px-4 py-3 text-gray-600">{formatDateTime(inv.PostedAt)}</td>
            <td className="px-4 py-3 text-gray-600">{inv.PostedByName || '—'}</td>
            <td className="px-4 py-3">
              {inv.ExternalPostingId ? (
                provider === 'xero' ? (
                  <a href={`https://go.xero.com/AccountsPayable/Edit.aspx?InvoiceID=${inv.ExternalPostingId}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono bg-blue-50 text-blue-700 rounded hover:bg-blue-100 border border-blue-200" title="View in Xero">
                    {inv.ExternalPostingId.substring(0, 8)}&hellip;
                    <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono bg-gray-100 text-gray-700 rounded">
                    {inv.ExternalPostingId}
                  </span>
                )
              ) : '—'}
            </td>
            <td className="px-4 py-3 text-right">
              <div className="flex items-center justify-end gap-1">
                <button onClick={() => handleRepost(inv.InvoiceID)} disabled={repostingIds.has(inv.InvoiceID)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-40"
                  title="Update in finance system">
                  {repostingIds.has(inv.InvoiceID) ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Pencil className="w-3 h-3" />}
                  Update
                </button>
                <button onClick={() => handleRevert(inv.InvoiceID)} disabled={revertingIds.has(inv.InvoiceID)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 disabled:opacity-40"
                  title="Revert">
                  {revertingIds.has(inv.InvoiceID) ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                  Revert
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS: Vendor Tables
// ═══════════════════════════════════════════════════════════════════════════════

function VendorReadyTable({ items, selectedIds, toggleSelect, toggleSelectAll, SortHeader, formatDate, visibleColumns }) {
  const col = (key) => !visibleColumns || visibleColumns.includes(key);
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="px-4 py-3 text-left w-10">
            <input type="checkbox" checked={items.length > 0 && selectedIds.size === items.length}
              onChange={toggleSelectAll} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          </th>
          <SortHeader field="VendorName">Vendor Name</SortHeader>
          {col('email') && <th className="px-4 py-3 text-left font-medium text-gray-600">Email</th>}
          {col('phone') && <th className="px-4 py-3 text-left font-medium text-gray-600">Phone</th>}
          {col('taxId') && <th className="px-4 py-3 text-left font-medium text-gray-600">Tax ID</th>}
          {col('paymentTerms') && <th className="px-4 py-3 text-left font-medium text-gray-600">Payment Terms</th>}
          {col('invoiceCount') && <th className="px-4 py-3 text-center font-medium text-gray-600">Invoices</th>}
          {col('createdAt') && <th className="px-4 py-3 text-left font-medium text-gray-600">Created</th>}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {items.length === 0 ? (
          <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">
            <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />No vendors ready to post
          </td></tr>
        ) : items.map((v, idx) => (
          <tr key={v.VendorID || idx} className={`hover:bg-gray-50 transition-colors ${selectedIds.has(v.VendorID) ? 'bg-blue-50/50' : ''}`}>
            <td className="px-4 py-3">
              <input type="checkbox" checked={selectedIds.has(v.VendorID)} onChange={() => toggleSelect(v.VendorID)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            </td>
            <td className="px-4 py-3 font-medium text-gray-900">{v.VendorName}</td>
            {col('email') && <td className="px-4 py-3 text-gray-600">{v.ContactEmail || '—'}</td>}
            {col('phone') && <td className="px-4 py-3 text-gray-600">{v.ContactPhone || '—'}</td>}
            {col('taxId') && <td className="px-4 py-3 text-gray-600">{v.TaxID || '—'}</td>}
            {col('paymentTerms') && <td className="px-4 py-3 text-gray-600">{v.PaymentTerms || '—'}</td>}
            {col('invoiceCount') && (
              <td className="px-4 py-3 text-center">
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-full">
                  {v.InvoiceCount || 0}
                </span>
              </td>
            )}
            {col('createdAt') && <td className="px-4 py-3 text-gray-600">{formatDate(v.CreatedAt)}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function VendorPostedTable({ items, selectedIds, toggleSelect, toggleSelectAll, formatDate, formatDateTime, handleRepost, handleRevert, repostingIds, revertingIds, provider, visibleColumns }) {
  const col = (key) => !visibleColumns || visibleColumns.includes(key);
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="px-4 py-3 text-left w-10">
            <input type="checkbox" checked={items.length > 0 && selectedIds.size === items.length}
              onChange={toggleSelectAll} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          </th>
          <th className="px-4 py-3 text-left font-medium text-gray-600">Vendor Name</th>
          {col('email') && <th className="px-4 py-3 text-left font-medium text-gray-600">Email</th>}
          {col('taxId') && <th className="px-4 py-3 text-left font-medium text-gray-600">Tax ID</th>}
          {col('invoiceCount') && <th className="px-4 py-3 text-center font-medium text-gray-600">Invoices</th>}
          {col('postedAt') && <th className="px-4 py-3 text-left font-medium text-gray-600">Posted</th>}
          {col('postedBy') && <th className="px-4 py-3 text-left font-medium text-gray-600">Posted By</th>}
          {col('externalId') && <th className="px-4 py-3 text-left font-medium text-gray-600">External ID</th>}
          <th className="px-4 py-3 text-right font-medium text-gray-600">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {items.length === 0 ? (
          <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-400">
            <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" />No posted vendors
          </td></tr>
        ) : items.map((v, idx) => (
          <tr key={v.VendorID || idx} className={`hover:bg-gray-50 transition-colors ${selectedIds.has(v.VendorID) ? 'bg-blue-50/50' : ''}`}>
            <td className="px-4 py-3">
              <input type="checkbox" checked={selectedIds.has(v.VendorID)} onChange={() => toggleSelect(v.VendorID)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            </td>
            <td className="px-4 py-3 font-medium text-gray-900">{v.VendorName}</td>
            {col('email') && <td className="px-4 py-3 text-gray-600">{v.ContactEmail || '—'}</td>}
            {col('taxId') && <td className="px-4 py-3 text-gray-600">{v.TaxID || '—'}</td>}
            {col('invoiceCount') && (
              <td className="px-4 py-3 text-center">
                <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-50 text-blue-700 rounded-full">
                  {v.InvoiceCount || 0}
                </span>
              </td>
            )}
            {col('postedAt') && <td className="px-4 py-3 text-gray-600">{formatDateTime(v.PostedAt)}</td>}
            {col('postedBy') && <td className="px-4 py-3 text-gray-600">{v.PostedByName || '—'}</td>}
            {col('externalId') && <td className="px-4 py-3">
              {v.ExternalVendorId ? (
                provider === 'xero' ? (
                  <a href={`https://go.xero.com/Contacts/View/${v.ExternalVendorId}`} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono bg-blue-50 text-blue-700 rounded hover:bg-blue-100 border border-blue-200" title="View in Xero">
                    {v.ExternalVendorId.substring(0, 8)}&hellip;
                    <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono bg-gray-100 text-gray-700 rounded">
                    {v.ExternalVendorId}
                  </span>
                )
              ) : '—'}
            </td>}
            <td className="px-4 py-3 text-right">
              <div className="flex items-center justify-end gap-1">
                <button onClick={() => handleRepost(v.VendorID)} disabled={repostingIds.has(v.VendorID)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-40"
                  title="Update in finance system">
                  {repostingIds.has(v.VendorID) ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Pencil className="w-3 h-3" />}
                  Update
                </button>
                <button onClick={() => handleRevert(v.VendorID)} disabled={revertingIds.has(v.VendorID)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 disabled:opacity-40"
                  title="Revert">
                  {revertingIds.has(v.VendorID) ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                  Revert
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


// ═══════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS: Expense Tables
// ═══════════════════════════════════════════════════════════════════════════════

function ExpenseReadyTable({ items, selectedIds, toggleSelect, toggleSelectAll, SortHeader, formatCurrency, formatDate, visibleColumns }) {
  const col = (key) => !visibleColumns || visibleColumns.includes(key);
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="px-4 py-3 text-left w-10">
            <input type="checkbox" checked={items.length > 0 && selectedIds.size === items.length}
              onChange={toggleSelectAll} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          </th>
          <SortHeader field="ExpenseNumber">Expense #</SortHeader>
          {col('employee') && <SortHeader field="EmployeeName">Employee</SortHeader>}
          {col('date') && <SortHeader field="ExpenseDate">Date</SortHeader>}
          {col('amount') && <SortHeader field="TotalAmount" className="text-right">Amount</SortHeader>}
          {col('currency') && <th className="px-4 py-3 text-left font-medium text-gray-600">Currency</th>}
          {col('purpose') && <th className="px-4 py-3 text-left font-medium text-gray-600">Purpose</th>}
          {col('category') && <th className="px-4 py-3 text-left font-medium text-gray-600">Category</th>}
          {col('department') && <th className="px-4 py-3 text-left font-medium text-gray-600">Department</th>}
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {items.length === 0 ? (
          <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-400">
            <CreditCard className="w-10 h-10 mx-auto mb-2 text-gray-300" />No approved expenses ready to post
          </td></tr>
        ) : items.map(exp => (
          <tr key={exp.ExpenseID} className={`hover:bg-gray-50 transition-colors ${selectedIds.has(exp.ExpenseID) ? 'bg-blue-50/50' : ''}`}>
            <td className="px-4 py-3">
              <input type="checkbox" checked={selectedIds.has(exp.ExpenseID)} onChange={() => toggleSelect(exp.ExpenseID)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            </td>
            <td className="px-4 py-3 font-medium text-gray-900">{exp.ExpenseNumber || '—'}</td>
            {col('employee') && <td className="px-4 py-3 text-gray-700">{exp.EmployeeName || '—'}</td>}
            {col('date') && <td className="px-4 py-3 text-gray-600">{formatDate(exp.ExpenseDate)}</td>}
            {col('amount') && <td className="px-4 py-3 text-right font-mono text-gray-900">{formatCurrency(exp.TotalAmount, exp.Currency)}</td>}
            {col('currency') && <td className="px-4 py-3 text-gray-600">{exp.Currency || 'GBP'}</td>}
            {col('purpose') && <td className="px-4 py-3 text-gray-600 max-w-[200px] truncate">{exp.Purpose || '—'}</td>}
            {col('category') && <td className="px-4 py-3 text-gray-600">{exp.Category || '—'}</td>}
            {col('department') && <td className="px-4 py-3 text-gray-600">{exp.DepartmentName || '—'}</td>}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function ExpensePostedTable({ items, selectedIds, toggleSelect, toggleSelectAll, formatCurrency, formatDate, formatDateTime, handleRepost, handleRevert, repostingIds, revertingIds, visibleColumns }) {
  const col = (key) => !visibleColumns || visibleColumns.includes(key);
  return (
    <table className="w-full text-sm">
      <thead className="bg-gray-50 border-b border-gray-200">
        <tr>
          <th className="px-4 py-3 text-left w-10">
            <input type="checkbox" checked={items.length > 0 && selectedIds.size === items.length}
              onChange={toggleSelectAll} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
          </th>
          <th className="px-4 py-3 text-left font-medium text-gray-600">Expense #</th>
          {col('employee') && <th className="px-4 py-3 text-left font-medium text-gray-600">Employee</th>}
          {col('date') && <th className="px-4 py-3 text-left font-medium text-gray-600">Date</th>}
          {col('amount') && <th className="px-4 py-3 text-right font-medium text-gray-600">Amount</th>}
          {col('purpose') && <th className="px-4 py-3 text-left font-medium text-gray-600">Purpose</th>}
          {col('postedAt') && <th className="px-4 py-3 text-left font-medium text-gray-600">Posted</th>}
          {col('postedBy') && <th className="px-4 py-3 text-left font-medium text-gray-600">Posted By</th>}
          {col('externalId') && <th className="px-4 py-3 text-left font-medium text-gray-600">External Ref</th>}
          <th className="px-4 py-3 text-right font-medium text-gray-600">Actions</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {items.length === 0 ? (
          <tr><td colSpan={10} className="px-4 py-12 text-center text-gray-400">
            <CreditCard className="w-10 h-10 mx-auto mb-2 text-gray-300" />No posted expenses
          </td></tr>
        ) : items.map(exp => (
          <tr key={exp.ExpenseID} className={`hover:bg-gray-50 transition-colors ${selectedIds.has(exp.ExpenseID) ? 'bg-blue-50/50' : ''}`}>
            <td className="px-4 py-3">
              <input type="checkbox" checked={selectedIds.has(exp.ExpenseID)} onChange={() => toggleSelect(exp.ExpenseID)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
            </td>
            <td className="px-4 py-3 font-medium text-gray-900">{exp.ExpenseNumber || '—'}</td>
            {col('employee') && <td className="px-4 py-3 text-gray-700">{exp.EmployeeName || '—'}</td>}
            {col('date') && <td className="px-4 py-3 text-gray-600">{formatDate(exp.ExpenseDate)}</td>}
            {col('amount') && <td className="px-4 py-3 text-right font-mono text-gray-900">{formatCurrency(exp.TotalAmount, exp.Currency)}</td>}
            {col('purpose') && <td className="px-4 py-3 text-gray-600 max-w-[160px] truncate">{exp.Purpose || '—'}</td>}
            {col('postedAt') && <td className="px-4 py-3 text-gray-600">{formatDateTime(exp.PostedAt)}</td>}
            {col('postedBy') && <td className="px-4 py-3 text-gray-600">{exp.PostedByName || '—'}</td>}
            {col('externalId') && <td className="px-4 py-3">
              {exp.ExternalExpenseId ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono bg-gray-100 text-gray-700 rounded">
                  {exp.ExternalExpenseId}
                </span>
              ) : '—'}
            </td>}
            <td className="px-4 py-3 text-right">
              <div className="flex items-center justify-end gap-1">
                <button onClick={() => handleRepost(exp.ExpenseID)} disabled={repostingIds.has(exp.ExpenseID)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 disabled:opacity-40"
                  title="Update in finance system">
                  {repostingIds.has(exp.ExpenseID) ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Pencil className="w-3 h-3" />}
                  Update
                </button>
                <button onClick={() => handleRevert(exp.ExpenseID)} disabled={revertingIds.has(exp.ExpenseID)}
                  className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 rounded hover:bg-red-100 disabled:opacity-40"
                  title="Revert">
                  {revertingIds.has(exp.ExpenseID) ? <RefreshCw className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                  Revert
                </button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
